import i18n from "../i18n";

export interface EmailValidationResult {
  isValid: boolean;
  error: string | null;
  normalizedEmail: string;
}

/**
 * Kompleksowa walidacja adresu email z polskimi komunikatami błędów
 */
export const validateEmail = (
  email: string
): EmailValidationResult => {
  // 1. Podstawowe czyszczenie
  const trimmedEmail = email.trim();
  const normalizedEmail = trimmedEmail.toLowerCase();

  // 2. Sprawdzenie pustego pola
  if (!normalizedEmail) {
    return {
      isValid: false,
      error: i18n.t("emailValidation.required"),
      normalizedEmail: "",
    };
  }

  // 3. Sprawdzenie długości (max 255 znaków zgodnie z RFC 5321)
  if (normalizedEmail.length > 255) {
    return {
      isValid: false,
      error: i18n.t("emailValidation.tooLong"),
      normalizedEmail,
    };
  }

  // 4. Sprawdzenie obecności dokładnie jednego @
  const atCount = (normalizedEmail.match(/@/g) || []).length;
  if (atCount === 0) {
    return {
      isValid: false,
      error: i18n.t("emailValidation.missingAt"),
      normalizedEmail,
    };
  }
  if (atCount > 1) {
    return {
      isValid: false,
      error: i18n.t("emailValidation.multipleAt"),
      normalizedEmail,
    };
  }

  // 5. Podział na lokalną część i domenę
  const [localPart, domain] = normalizedEmail.split("@");

  // 6. Walidacja lokalnej części (przed @)
  if (!localPart || localPart.length === 0) {
    return {
      isValid: false,
      error: i18n.t("emailValidation.missingLocalPart"),
      normalizedEmail,
    };
  }

  if (localPart.length > 64) {
    return {
      isValid: false,
      error: i18n.t("emailValidation.localPartTooLong"),
      normalizedEmail,
    };
  }

  // Sprawdzenie czy nie zaczyna się ani nie kończy kropką
  if (localPart.startsWith(".") || localPart.endsWith(".")) {
    return {
      isValid: false,
      error: i18n.t("emailValidation.localPartDotEdge"),
      normalizedEmail,
    };
  }

  // Sprawdzenie podwójnych kropek
  if (localPart.includes("..")) {
    return {
      isValid: false,
      error: i18n.t("emailValidation.localPartDoubleDot"),
      normalizedEmail,
    };
  }

  // Sprawdzenie dozwolonych znaków w lokalnej części
  const localPartRegex = /^[a-z0-9!#$%&'*+/=?^_`{|}~.-]+$/;
  if (!localPartRegex.test(localPart)) {
    return {
      isValid: false,
      error: i18n.t("emailValidation.localPartInvalidChars"),
      normalizedEmail,
    };
  }

  // 7. Walidacja domeny (po @)
  if (!domain || domain.length === 0) {
    return {
      isValid: false,
      error: i18n.t("emailValidation.missingDomain"),
      normalizedEmail,
    };
  }

  if (domain.length > 253) {
    return {
      isValid: false,
      error: i18n.t("emailValidation.domainTooLong"),
      normalizedEmail,
    };
  }

  // Sprawdzenie czy nie zaczyna się ani nie kończy kropką lub myślnikiem
  if (domain.startsWith(".") || domain.endsWith(".")) {
    return {
      isValid: false,
      error: i18n.t("emailValidation.domainDotEdge"),
      normalizedEmail,
    };
  }

  if (domain.startsWith("-") || domain.endsWith("-")) {
    return {
      isValid: false,
      error: i18n.t("emailValidation.domainHyphenEdge"),
      normalizedEmail,
    };
  }

  // Sprawdzenie podwójnych kropek w domenie
  if (domain.includes("..")) {
    return {
      isValid: false,
      error: i18n.t("emailValidation.domainDoubleDot"),
      normalizedEmail,
    };
  }

  // Sprawdzenie czy domena zawiera przynajmniej jedną kropkę
  if (!domain.includes(".")) {
    return {
      isValid: false,
      error: i18n.t("emailValidation.domainNoExtension"),
      normalizedEmail,
    };
  }

  // Sprawdzenie dozwolonych znaków w domenie
  const domainRegex = /^[a-z0-9.-]+$/;
  if (!domainRegex.test(domain)) {
    return {
      isValid: false,
      error: i18n.t("emailValidation.domainInvalidChars"),
      normalizedEmail,
    };
  }

  // Sprawdzenie czy każda część domeny (między kropkami) jest poprawna
  const domainParts = domain.split(".");
  for (const part of domainParts) {
    if (part.length === 0) {
      return {
        isValid: false,
        error: i18n.t("emailValidation.domainInvalidStructure"),
        normalizedEmail,
      };
    }
    if (part.length > 63) {
      return {
        isValid: false,
        error: i18n.t("emailValidation.domainPartTooLong"),
        normalizedEmail,
      };
    }
    if (part.startsWith("-") || part.endsWith("-")) {
      return {
        isValid: false,
        error: i18n.t("emailValidation.domainPartHyphenEdge"),
        normalizedEmail,
      };
    }
  }

  // Sprawdzenie czy ostatnia część domeny (TLD) ma minimum 2 znaki i składa się tylko z liter
  const tld = domainParts[domainParts.length - 1];
  if (tld.length < 2) {
    return {
      isValid: false,
      error: i18n.t("emailValidation.tldTooShort"),
      normalizedEmail,
    };
  }
  if (!/^[a-z]+$/.test(tld)) {
    return {
      isValid: false,
      error: i18n.t("emailValidation.tldLettersOnly"),
      normalizedEmail,
    };
  }

  // 8. Wszystko OK
  return {
    isValid: true,
    error: null,
    normalizedEmail,
  };
};
